import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabNavigation, type TabConfig } from '../../components/TabNavigation';
import { QuestionsList } from './components/QuestionsList';
import { ImportQuestions } from './components/ImportQuestions';
import type { CommunityQuestionsTabType } from './types/tabs';

const tabs: TabConfig<CommunityQuestionsTabType>[] = [
  { id: 'questions', label: 'Questions', icon: '📋' },
  { id: 'import', label: 'Import CSV', icon: '📥' },
];

const isValidTab = (tab: string | null): tab is CommunityQuestionsTabType => {
  return tab === 'questions' || tab === 'import';
};

export default function CommunityQuestions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<CommunityQuestionsTabType>('questions');

  // Initialize tab from URL on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (isValidTab(tabParam)) {
      setActiveTab(tabParam);
    } else if (tabParam) {
      // Invalid tab in URL, reset to questions
      setSearchParams({ tab: 'questions' }, { replace: true });
    } else {
      // No tab in URL, set default
      setSearchParams({ tab: 'questions' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleTabChange = (tab: CommunityQuestionsTabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Community Questions</h1>
          <p className="mt-2 text-gray-600">Manage community questions and polls</p>
        </div>

        {/* Tab Navigation */}
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

        {/* Questions List Tab */}
        {activeTab === 'questions' && <QuestionsList />}

        {/* Import CSV Tab */}
        {activeTab === 'import' && <ImportQuestions />}
      </div>
    </div>
  );
}
